import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FaPlus, FaPencilAlt, FaBan, FaCheck, FaArrowLeft, FaCalendarAlt } from "react-icons/fa";
import Modal from "../Modal";

const API = "/api/eventos";

const formatoFecha = (fecha) =>
  fecha ? new Date(fecha).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "";

export default function EventosAdmin() {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ titulo: "", descripcion: "", fecha_inicio: "", fecha_fin: "" });
  const [imagenFile, setImagenFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API, { credentials: "include" });
      if (!res.ok) throw new Error("Error obteniendo eventos");
      setEventos(await res.json());
    } catch (err) {
      console.error(err);
      mostrarToast("Error cargando eventos", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirModal = () => {
    setEditando(null);
    setForm({ titulo: "", descripcion: "", fecha_inicio: "", fecha_fin: "" });
    setImagenFile(null);
    setPreview(null);
    setModalOpen(true);
  };

  const abrirModalEditar = (evento) => {
    setEditando(evento);
    setForm({
      titulo: evento.titulo,
      descripcion: evento.descripcion || "",
      fecha_inicio: evento.fecha_inicio?.slice(0, 10) || "",
      fecha_fin: evento.fecha_fin?.slice(0, 10) || "",
    });
    setImagenFile(null);
    setPreview(evento.imagen ? `${evento.imagen}` : null);
    setModalOpen(true);
  };

  const cerrarModal = () => setModalOpen(false);

  const onSeleccionarImagen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImagenFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const guardar = async () => {
    if (!form.titulo.trim() || !form.fecha_inicio || !form.fecha_fin) {
      mostrarToast("Título, fecha de inicio y fecha de fin son obligatorios", "error");
      return;
    }

    if (form.fecha_fin < form.fecha_inicio) {
      mostrarToast("La fecha de fin no puede ser anterior a la de inicio", "error");
      return;
    }

    const data = new FormData();
    data.append("titulo", form.titulo);
    data.append("descripcion", form.descripcion);
    data.append("fecha_inicio", form.fecha_inicio);
    data.append("fecha_fin", form.fecha_fin);
    if (imagenFile) data.append("imagen", imagenFile);

    try {
      const res = await fetch(editando ? `${API}/${editando.id_evento}` : API, {
        method: editando ? "PUT" : "POST",
        credentials: "include",
        body: data,
      });

      if (!res.ok) {
        const err = await res.json();
        mostrarToast(err.mensaje || "Error guardando evento", "error");
        return;
      }

      mostrarToast(editando ? "Evento actualizado" : "Evento creado");
      setModalOpen(false);
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    }
  };

  const cambiarEstado = async (evento) => {
    const activar = !evento.activo;
    try {
      const res = await fetch(`${API}/${evento.id_evento}${activar ? "/reactivar" : ""}`, {
        method: activar ? "PATCH" : "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json();
        mostrarToast(err.mensaje || "Error actualizando evento", "error");
        return;
      }

      mostrarToast(activar ? "Evento reactivado" : "Evento desactivado");
      cargar();
    } catch (err) {
      console.error(err);
      mostrarToast("Error inesperado", "error");
    }
  };

  const hoy = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-6 bg-slate-50">

      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/dashboard/administracion" className="text-sm text-slate-500 hover:text-indigo-600 flex items-center gap-1 mb-1">
            <FaArrowLeft className="text-xs" /> Administración
          </Link>
          <h1 className="text-2xl font-bold text-slate-800">Eventos</h1>
          <p className="text-sm text-slate-500">
            Los eventos vigentes (que aún no terminan) se muestran automáticamente en el dashboard.
          </p>
        </div>

        <button
          onClick={abrirModal}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
        >
          <FaPlus /> Nuevo evento
        </button>
      </div>

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={cerrarModal}
        title={editando ? "Editar evento" : "Nuevo evento"}
        maxWidth="max-w-3xl"
      >
        <div>
          <div className="grid md:grid-cols-2 gap-6">

            {/* IMAGEN */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Imagen del evento</label>
              <div className="w-full aspect-[16/9] bg-slate-100 rounded-lg border border-slate-200 overflow-hidden flex items-center justify-center">
                {preview ? (
                  <img src={preview} alt="preview" className="w-full h-full object-contain" />
                ) : (
                  <FaCalendarAlt className="text-4xl text-slate-300" />
                )}
              </div>
              <input
                type="file"
                accept="image/png, image/jpeg, image/webp"
                onChange={onSeleccionarImagen}
                className="mt-2 text-sm text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-indigo-50 file:text-indigo-700 file:text-xs file:font-medium hover:file:bg-indigo-100"
              />
              <p className="text-xs text-slate-400 mt-1.5">
                Usa una imagen horizontal. Tamaño recomendado: 1200 x 675 px (relación 16:9). Formatos JPG, PNG o WEBP.
              </p>
            </div>

            {/* CAMPOS */}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Título del evento <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Título del evento"
                  className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                  value={form.titulo}
                  onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Descripción</label>
                <textarea
                  placeholder="Descripción (opcional)"
                  rows={3}
                  className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 resize-none"
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Fecha de inicio <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    value={form.fecha_inicio}
                    onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Fecha de fin <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                    value={form.fecha_fin}
                    onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
            <button onClick={cerrarModal} className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition">
              Cancelar
            </button>
            <button onClick={guardar} className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition">
              {editando ? "Actualizar" : "Guardar"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Lista */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <p className="text-sm text-slate-400 col-span-full text-center py-10">Cargando eventos...</p>
        ) : eventos.length === 0 ? (
          <p className="text-sm text-slate-400 col-span-full text-center py-10">Aún no hay eventos registrados.</p>
        ) : (
          eventos.map((ev) => {
            const vigente = ev.activo && ev.fecha_fin?.slice(0, 10) >= hoy;
            return (
              <div key={ev.id_evento} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                <div className="h-32 bg-slate-100 flex items-center justify-center overflow-hidden">
                  {ev.imagen ? (
                    <img src={`${ev.imagen}`} alt={ev.titulo} className="w-full h-full object-cover" />
                  ) : (
                    <FaCalendarAlt className="text-3xl text-slate-300" />
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="font-semibold text-sm text-slate-800">{ev.titulo}</p>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${
                      !ev.activo ? "bg-slate-100 text-slate-500" : vigente ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
                    }`}>
                      {!ev.activo ? "Inactivo" : vigente ? "Vigente" : "Vencido"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    {formatoFecha(ev.fecha_inicio)} — {formatoFecha(ev.fecha_fin)}
                  </p>

                  <div className="mt-auto flex gap-2">
                    <button
                      onClick={() => abrirModalEditar(ev)}
                      className="flex-1 flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                    >
                      <FaPencilAlt className="text-[10px]" /> Editar
                    </button>
                    <button
                      onClick={() => cambiarEstado(ev)}
                      className={`flex-1 flex items-center justify-center gap-1 text-xs px-3 py-1.5 rounded-md border transition ${
                        ev.activo
                          ? "border-rose-200 text-rose-600 hover:bg-rose-50"
                          : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                      }`}
                    >
                      {ev.activo ? <><FaBan className="text-[10px]" /> Desactivar</> : <><FaCheck className="text-[10px]" /> Activar</>}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
